"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { loginSchema } from "../validations/client-validation";
import { ArrowRight, Dumbbell, Lock, Mail } from "lucide-react";
import FormInput from "@/shared/components/ui/form-input";
import { Button } from "@/shared/components/ui/button";
import Link from "next/link";
import { useActionHandler } from "@/shared/hooks/use-action-handler";
import { loginAction, tenantLoginAction } from "../auth-actions";
import { Spinner } from "@/shared/components/ui/spinner";
import { useSearchParams } from "next/navigation";
import { Route } from "next";
import { resolveRedirectPath } from "@/shared/lib/utils";
import Image from "next/image";
import { useEffect } from "react";
import { toast } from "sonner";
import { SIGNED_OUT_REASONS, SIGNED_OUT_REASON_PARAM, type SignedOutReason } from "@/shared/constants/auth-cookies";
interface Tenant {
  name: string;
  logo?: string;
}
type LoginFormProps = {
  mode: "default";
} | {
  mode: "tenant";
  tenant: Tenant;
};
const LoginForm = props => {
  const $ = _c(51);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = zodResolver(loginSchema);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: t0,
      mode: "onSubmit",
      defaultValues: {
        email: "",
        password: ""
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    control,
    handleSubmit,
    reset,
    formState,
    setFocus
  } = useForm(t1);
  const {
    handleAction
  } = useActionHandler();
  const isLoading = formState.isSubmitting;
  const params = useSearchParams();
  let t2;
  if ($[2] !== params) {
    t2 = params.get(SIGNED_OUT_REASON_PARAM);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const reason = t2;
  let t3;
  let t4;
  if ($[4] !== reason) {
    t3 = () => {
      const message = SIGNED_OUT_REASONS[reason as SignedOutReason];
      if (message) {
        toast.error(message);
      }
    };
    t4 = [reason];
    $[4] = reason;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== handleAction || $[8] !== params || $[9] !== props.mode || $[10] !== reset || $[11] !== setFocus) {
    t5 = async data => {
      const redirectTo = resolveRedirectPath(params.get("redirectTo") ?? "", props.mode === "tenant" ? "/members" : "/dashboard") as Route;
      await handleAction(props.mode === "tenant" ? tenantLoginAction : loginAction, {
        values: data,
        redirectTo
      }, {
        onSuccess: () => {
          reset();
        },
        onError: () => {
          setFocus("password");
        }
      });
    };
    $[7] = handleAction;
    $[8] = params;
    $[9] = props.mode;
    $[10] = reset;
    $[11] = setFocus;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== handleSubmit || $[14] !== t5) {
    t6 = handleSubmit(t5);
    $[13] = handleSubmit;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onSubmit = t6;
  let t7;
  if ($[16] !== props.mode || $[17] !== props.tenant) {
    t7 = <div className="inline-flex items-center justify-center w-20 h-20  rounded-3xl mb-6 bg-background">{props.mode === "tenant" && props.tenant.logo ? <Image src={props.tenant.logo} alt="Logo" className="w-10 h-10" /> : <Link href="/" className="inline-flex items-center justify-center w-20 h-20 bg-brand-primary/10  bg-primary rounded-full"><Dumbbell className="w-10 h-10 text-brand-primary -rotate-45 hover:rotate-180 transition-transform duration-500 text-accent" /></Link>}</div>;
    $[16] = props.mode;
    $[17] = props.tenant;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h2 className="text-3xl  tracking-tight mb-2">Login</h2>;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = `Enter your details to login into ${props.mode === "tenant" ? props.tenant.name : "GymMart"}.`;
  let t10;
  if ($[20] !== t9) {
    t10 = <p className="text-muted-foreground text-sm ">{t9}</p>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className="text-center">{t7}{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Mail size={18} />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== control || $[27] !== isLoading) {
    t13 = <FormInput label="Email" name="email" type="text" placeholder="example@email.com" icon={t12} control={control} disabled={isLoading} />;
    $[26] = control;
    $[27] = isLoading;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Lock size={18} />;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== control || $[31] !== isLoading) {
    t15 = <FormInput type="password" placeholder="*********" icon={t14} control={control} label="Password" name="password" disabled={isLoading} />;
    $[30] = control;
    $[31] = isLoading;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex justify-end -mt-2"><Link href="/forgot-password" className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors">Forgot password?</Link></div>;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span>Login</span>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isLoading) {
    t18 = isLoading ? <Spinner className="text-accent" /> : <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform text-accent" />;
    $[35] = isLoading;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== isLoading || $[38] !== t18) {
    t19 = <Button type="submit" className="group text-accent w-full flex items-center justify-center gap-2 py-4 px-6     transition-all duration-200 transform active:scale-[0.98]  " disabled={isLoading}>{t17}{t18}</Button>;
    $[37] = isLoading;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== onSubmit || $[41] !== t13 || $[42] !== t15 || $[43] !== t19) {
    t20 = <form onSubmit={onSubmit} className="space-y-6">{t13}{t15}{t16}{t19}</form>;
    $[40] = onSubmit;
    $[41] = t13;
    $[42] = t15;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== props.mode) {
    t21 = props.mode === "default" && <div className="text-center mt-6"><p className="text-sm text-muted-foreground">Don't have an account?{" "}<Link href="/register" className="font-semibold text-primary hover:text-brand-primary hover:underline transition-colors">Register</Link></p></div>;
    $[45] = props.mode;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t11 || $[48] !== t20 || $[49] !== t21) {
    t22 = <div className="w-full max-w-110 flex flex-col space-y-2 py-8 px-2 md:px-0">{t11}{t20}{t21}</div>;
    $[47] = t11;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return t22;
};
export default LoginForm;